import type { SourceTurnV1, TranslationRevisionV1 } from '@/lib/translation/contracts';
import type { LaunchLanguageCode } from '@/lib/translation/launchLanguages';
import type { RevisionUnavailableReasonCode } from '@/lib/translation/revisionEnvelope';

/** The v1 browser snapshot assembled from bounded v2 database pages. */
export interface CaptionHydrationSnapshot {
  contract_version: 'live-translation.caption-hydration.v1';
  call_id: string;
  target_language: LaunchLanguageCode;
  source_turns: { source_turn: SourceTurnV1; transcript_sync_id: string | null }[];
  revisions: TranslationRevisionV1[];
  selections: {
    source_turn_id: string;
    target_language: LaunchLanguageCode;
    selected_revision_id: string;
    selection_generation: number;
    selected_revision_index: number;
  }[];
  unavailable: {
    source_turn_id: string;
    target_language: LaunchLanguageCode;
    reason_code: RevisionUnavailableReasonCode;
  }[];
  authorized_worker_identities: string[];
}

/**
 * One limits object is imported by both the server assembler and browser
 * boundary. The database independently enforces a maximum page size of 128;
 * the route deliberately asks for fewer rows so one worst-case text page stays
 * comfortably below the per-page byte ceiling.
 */
export const CAPTION_HYDRATION_LIMITS = Object.freeze({
  responseBytes: 8 * 1024 * 1024,
  rpcPageBytes: 3 * 1024 * 1024,
  rpcPageSize: 100,
  rpcMaxPageSize: 128,
  maxPages: 256,
  maxTotalRows: 20_480,
  sourceTurns: 4096,
  revisions: 8192,
  selections: 4096,
  unavailable: 4096,
  authorizedWorkerIdentities: 256,
  transcriptSyncIdChars: 512,
  referenceIdChars: 128,
  workerIdentityChars: 256,
  visibilitySnapshotChars: 65_536,
  producerTrustValidityMs: 300_000,
  /** The database/route never exposes a shorter producer authority. */
  producerTrustServerMinimumValidityMs: 10_000,
  /** A response is refused if request latency leaves less local authority. */
  producerTrustClientMinimumValidityMs: 5_000,
  /** Reserved between browser revocation and the underlying room lease expiry. */
  producerTrustTransportMarginMs: 5_000,
  /** One deadline covers fetch plus bounded response-body consumption. */
  requestTimeoutMs: 60_000,
});

/** Build a replacement trust set. The prior set is intentionally not accepted. */
export function replacementCaptionHydrationWorkerIdentities(
  snapshot: Pick<CaptionHydrationSnapshot, 'authorized_worker_identities'>,
): ReadonlySet<string> {
  return new Set(snapshot.authorized_worker_identities);
}

export type CaptionHydrationClock = () => number;

/** Wall time covers suspend on platforms whose monotonic clock pauses. */
export const captionHydrationWallNowMs: CaptionHydrationClock = () => Date.now();

/** Monotonic time prevents a wall-clock rollback from extending authority. */
export const captionHydrationMonotonicNowMs: CaptionHydrationClock = () =>
  typeof performance === 'undefined' ? Date.now() : performance.now();

/**
 * Elapsed request time is the most conservative nonnegative observation. A
 * wall rollback cannot erase monotonic latency, and a paused monotonic clock
 * cannot erase wall time spent suspended.
 */
export function captionHydrationElapsedMs(
  wallStartedAtMs: number,
  monotonicStartedAtMs: number,
  wallFinishedAtMs: number,
  monotonicFinishedAtMs: number,
): number {
  return Math.max(
    0,
    wallFinishedAtMs - wallStartedAtMs,
    monotonicFinishedAtMs - monotonicStartedAtMs,
  );
}

/** Own both local deadlines after which producer authority must be hydrated
 * again. Replacing a trusted snapshot atomically replaces its timers and
 * generation. Authority expires as soon as either independent clock expires. */
export class CaptionProducerTrustFreshness {
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private monotonicDeadlineMs: number | null = null;
  private wallDeadlineMs: number | null = null;
  private onExpired: (() => void) | null = null;

  constructor(
    private readonly monotonicNowMs: CaptionHydrationClock = captionHydrationMonotonicNowMs,
    private readonly wallNowMs: CaptionHydrationClock = captionHydrationWallNowMs,
  ) {}

  replace(validForMs: number, onExpired: () => void): void {
    this.replaceDeadlines(this.monotonicNowMs() + validForMs,
      this.wallNowMs() + validForMs, onExpired, validForMs);
  }

  /** Preserve the ORIGINAL request horizons, including transport and all
   * synchronous validation/installation time. Never rebase a stale remainder
   * on newly sampled clocks, which would extend current sender authority. */
  replaceFromRequestStart(
    validForMs: number,
    startedAtWallMs: number,
    startedAtMonotonicMs: number,
    onExpired: () => void,
  ): void {
    this.replaceDeadlines(startedAtMonotonicMs + validForMs,
      startedAtWallMs + validForMs, onExpired);
  }

  private replaceDeadlines(
    monotonicDeadlineMs: number,
    wallDeadlineMs: number,
    onExpired: () => void,
    initialDelayMs?: number,
  ): void {
    this.clearTimer();
    this.generation += 1;
    const generation = this.generation;
    this.monotonicDeadlineMs = monotonicDeadlineMs;
    this.wallDeadlineMs = wallDeadlineMs;
    this.onExpired = onExpired;
    const runAtDeadline = () => {
      if (generation !== this.generation) return;
      this.timer = null;
      if (this.expireIfNeeded()) return;
      // A scheduler is allowed to wake fractionally early. Re-arm the earliest
      // independent remainder instead of losing expiry for the rest of the call.
      if (this.monotonicDeadlineMs !== null && this.wallDeadlineMs !== null) {
        const remainingMs = Math.min(
          Math.max(0, this.monotonicDeadlineMs - this.monotonicNowMs()),
          Math.max(0, this.wallDeadlineMs - this.wallNowMs()),
        );
        this.timer = setTimeout(runAtDeadline, remainingMs);
      }
    };
    const remainingMs = initialDelayMs ?? Math.min(
      Math.max(0, monotonicDeadlineMs - this.monotonicNowMs()),
      Math.max(0, wallDeadlineMs - this.wallNowMs()),
    );
    this.timer = setTimeout(runAtDeadline, remainingMs);
  }

  /**
   * Revoke against the absolute local deadline, not merely when a timer happens
   * to run. Every sender evaluation calls this first, so a throttled/background
   * tab cannot retain expired producer authority while its timer is delayed.
   */
  expireIfNeeded(): boolean {
    if (
      this.monotonicDeadlineMs === null ||
      this.wallDeadlineMs === null ||
      (this.monotonicNowMs() < this.monotonicDeadlineMs && this.wallNowMs() < this.wallDeadlineMs)
    ) {
      return false;
    }
    const expired = this.onExpired;
    this.generation += 1;
    this.clearTimer();
    this.monotonicDeadlineMs = null;
    this.wallDeadlineMs = null;
    this.onExpired = null;
    expired?.();
    return true;
  }

  clear(): void {
    this.generation += 1;
    this.clearTimer();
    this.monotonicDeadlineMs = null;
    this.wallDeadlineMs = null;
    this.onExpired = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}

/** Bound sender-refusal driven hydration storms while still reacting to a
 * producer rotation immediately on the first authenticated rejection. */
export class CaptionHydrationStormGate {
  private nextAllowedMonotonicMs: number | null = null;
  private nextAllowedWallMs: number | null = null;

  constructor(
    private readonly intervalMs: number,
    private readonly monotonicNowMs: CaptionHydrationClock = captionHydrationMonotonicNowMs,
    private readonly wallNowMs: CaptionHydrationClock = captionHydrationWallNowMs,
  ) {}

  allow(nowMs?: number, monotonicNowMs?: number): boolean {
    // The optional numeric arguments retain the original deterministic test API.
    // When only one is supplied it represents both clocks.
    const wallNow = nowMs ?? this.wallNowMs();
    const monotonicNow = monotonicNowMs ?? nowMs ?? this.monotonicNowMs();
    if (
      this.nextAllowedWallMs !== null &&
      this.nextAllowedMonotonicMs !== null &&
      wallNow < this.nextAllowedWallMs &&
      monotonicNow < this.nextAllowedMonotonicMs
    ) {
      return false;
    }
    // Do not move either independent deadline backwards. In particular, a
    // permit granted by monotonic time during a wall rollback must not make a
    // subsequent wall-clock recovery look like another complete interval.
    this.nextAllowedWallMs = Math.max(wallNow, this.nextAllowedWallMs ?? wallNow) + this.intervalMs;
    this.nextAllowedMonotonicMs =
      Math.max(monotonicNow, this.nextAllowedMonotonicMs ?? monotonicNow) + this.intervalMs;
    return true;
  }

  clear(): void {
    this.nextAllowedWallMs = null;
    this.nextAllowedMonotonicMs = null;
  }
}
